import {
    IBankAccountResponse,
    IExchangePublicTokenResponse,
    IPaymentMethodResponse
} from '~/types/server.types';
import {
    ICreateChargeableBankAccount,
    ICreateLinkToken,
    ICreatePaymentMethod,
    IExchangePublicToken,
    IGetPlaidAccounts,
    ILinkTokenResponse,
    IReportPlaidEvent
} from '~/components/ClientExperience/services/pay.service';
import { IOnEventMetadata } from '~/components/common/Stripe/usePlaid';
import { merge } from 'lodash';
import { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';

export interface IPayService {
    createPaymentMethod(
        params: ICreatePaymentMethod
    ): Promise<IPaymentMethodResponse>;

    createChargeableBankAccount(
        params: ICreateChargeableBankAccount
    ): Promise<IPaymentMethodResponse>;

    createLinkToken(params: ICreateLinkToken): Promise<ILinkTokenResponse>;

    exchangePublicToken(
        params: IExchangePublicToken
    ): Promise<IExchangePublicTokenResponse>;

    reportPlaidEvent(params: IReportPlaidEvent): Promise<void>;

    connectPlaidAccount(
        params: IGetPlaidAccounts
    ): Promise<IBankAccountResponse[]>;
}

type IBatchedPlaidEvents = {
    eventName: string;
    metadata: IOnEventMetadata;
}[];

type GetApiUrlParams = {
    flowId: string;
    userId: string;
};

export abstract class AbstractPaymentMethodService implements IPayService {
    globalContext: AuthenticatedGlobalContextValue;
    batchedPlaidEvents: IBatchedPlaidEvents = null;
    reportPlaidEventPromise: ReturnType<typeof setTimeout> = null;

    get apiService() {
        return this.globalContext.apiService;
    }

    constructor({
        globalContext
    }: {
        globalContext: AuthenticatedGlobalContextValue;
    }) {
        this.globalContext = globalContext;
    }

    protected abstract createPaymentMethodUrl(params: GetApiUrlParams): string;

    async createPaymentMethod({
        userId,
        vendorId,
        stripeToken,
        cardRole,
        paymentMethodType,
        flowId,
        stripePaymentMethod
    }: ICreatePaymentMethod) {
        const data = {
            ...(stripeToken?.card || stripePaymentMethod?.card),
            token_id: stripeToken?.id || stripePaymentMethod?.id,
            primary_source: !!stripePaymentMethod
                ? 'payment_element'
                : 'card_element',
            secondary_source:
                stripePaymentMethod?.card?.wallet?.type || 'manual',
            vendor_id: vendorId,
            card_role: cardRole,
            _type: paymentMethodType,
            flow_id: flowId
        };

        // PaymentElement only
        if (stripePaymentMethod) {
            const {
                billing_details: {
                    address: {
                        city: address_city,
                        country: address_country,
                        line1: address_line1,
                        line2: address_line2,
                        postal_code: address_zip,
                        state: address_state
                    }
                }
            } = stripePaymentMethod;

            merge(data, {
                address_line1,
                address_line2,
                address_city,
                address_zip,
                address_state,
                address_country
            });
        }

        return this.apiService.apiRequest<IPaymentMethodResponse>({
            method: 'post',
            url: this.createPaymentMethodUrl({ flowId, userId }),
            data
        });
    }

    protected abstract createChargeableBankAccountUrl(
        params: GetApiUrlParams
    ): string;

    async createChargeableBankAccount({
        account,
        userId,
        bankRole = null,
        account_key,
        flowId = null,
        institutionId,
        institutionName
    }: ICreateChargeableBankAccount) {
        return this.apiService.apiRequest<IPaymentMethodResponse>({
            method: 'post',
            url: this.createChargeableBankAccountUrl({ flowId, userId }),
            data: {
                account,
                account_id: account[account_key],
                bank_role: bankRole,
                flow_id: flowId,
                institution_id: institutionId,
                institution_name: institutionName
            }
        });
    }

    protected abstract createLinkTokenUrl(params: GetApiUrlParams): string;

    async createLinkToken({
        flowId,
        userId,
        plaidProducts,
        redirectUri
    }: ICreateLinkToken): Promise<ILinkTokenResponse> {
        return this.apiService.apiRequest<ILinkTokenResponse>({
            method: 'post',
            url: this.createLinkTokenUrl({ flowId, userId }),
            data: {
                plaid_products: plaidProducts,
                redirect_uri: redirectUri
            }
        });
    }

    protected abstract exchangePublicTokenUrl(params: GetApiUrlParams): string;

    async exchangePublicToken({
        flowId,
        userId,
        publicToken,
        institutionId,
        institutionName
    }: IExchangePublicToken): Promise<IExchangePublicTokenResponse> {
        return this.apiService.apiRequest<IExchangePublicTokenResponse>({
            method: 'post',
            url: this.exchangePublicTokenUrl({ flowId, userId }),
            data: {
                token: publicToken,
                institution_id: institutionId,
                institution_name: institutionName
            }
        });
    }

    async connectPlaidAccount({
        userId,
        src,
        institutionId,
        institutionName
    }: IGetPlaidAccounts): Promise<IBankAccountResponse[]> {
        return this.apiService.apiRequest<IBankAccountResponse[]>({
            method: 'post',
            url: `/api/v2/users/${userId}/plaid_account`,
            data: {
                src,
                institution_id: institutionId,
                institution_name: institutionName
            }
        });
    }

    protected abstract plaidEventUrl(params: GetApiUrlParams): string;

    async reportPlaidEvent({
        flowId,
        eventName,
        metadata,
        userId
    }: IReportPlaidEvent): Promise<void> {
        const event = { eventName, metadata };
        if (this.batchedPlaidEvents) {
            this.batchedPlaidEvents.push(event);
        } else {
            this.batchedPlaidEvents = [event];
            clearTimeout(this.reportPlaidEventPromise);
            this.reportPlaidEventPromise = setTimeout(() => {
                this.apiService.apiRequest({
                    method: 'post',
                    url: this.plaidEventUrl({ flowId, userId }),
                    data: {
                        events: this.batchedPlaidEvents
                    }
                });
                this.batchedPlaidEvents = null;
            }, 50);
        }
    }
}
